import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/**
 * LogoScrollMorph — landing-page-only animated wordmark.
 *
 * Implements the "devour, refined" design from
 * design/Wordmark scroll-morph - Devour refined.html (handoff bundle
 * lembra-mascot/, May 2026, 4-chat iteration). The full v2.6 spec
 * lives in that file's `<style>` block — this component recreates
 * the same DOM + transitions in React/Tailwind without copying the
 * prototype's outer chrome.
 *
 * What it does:
 *   At rest the wordmark reads "lembra." in DM Sans 700 navy on
 *   cream — the period IS a tiny navy dot sitting flush with the
 *   "a". When the page scrolls past 60px (ENTER threshold), the
 *   letters fade right-to-left (a → l) with a 30ms-per-glyph
 *   stagger, while the period grows into a full navy disc. Inside
 *   the disc, an italic butter-coloured "l" (Newsreader 600) fades
 *   up at 320ms — mirroring the shape of the favicon. When the
 *   page scrolls back below 40px (EXIT threshold, hysteresis), the
 *   whole thing unwinds in reverse.
 *
 * Why a separate component (not extending Logo):
 *   - The existing <Logo> renders an <img src="/logo-navy.svg" />.
 *     The morph needs DOM-level control over every letter + the
 *     dot, so the wordmark has to be typographic, not raster.
 *   - This component is landing-specific. Dashboard, settings,
 *     vendor, blog etc. keep the existing <Logo>. Splitting
 *     prevents the scroll listener from running on every route.
 *
 * Design tokens (from lembra-tokens.css):
 *   - --navy: #1D3461        wordmark + dot fill
 *   - --butter: #F7E8A4      italic l inside the grown dot
 *   - --ease-lembra: cubic-bezier(0.16, 1, 0.3, 1)
 *   - duration: 620ms for the structural transitions
 *   - letter stagger: 30ms per glyph, right-to-left
 *   - italic l reveal: 320ms delay after collapse triggers
 *
 * Accessibility:
 *   - aria-label="Lembra, home" on the link (matches Logo.tsx)
 *   - aria-hidden="true" on the morphing italic l (decorative)
 *   - prefers-reduced-motion zeroes the transitions globally via
 *     a tailwind-level media query on the wrapper.
 *
 * Click behaviour:
 *   Mirrors Logo.tsx — navigates to /dashboard if signed-in,
 *   otherwise /. On the landing page (already at /), smooth-
 *   scrolls to top, which also triggers the expand-back animation
 *   naturally as scrollY drops below 40.
 */

const ENTER = 60;
const EXIT = 40;

interface LogoScrollMorphProps {
  className?: string;
}

export function LogoScrollMorph({ className }: LogoScrollMorphProps) {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  const logoDestination = user ? "/dashboard" : "/";

  useEffect(() => {
    // Initial state from current scroll position (covers the case
    // where the user navigates to / from a deep-scroll position
    // back-button, or refreshes mid-scroll).
    const initialY = window.scrollY;
    if (initialY > ENTER) {
      collapsedRef.current = true;
      setCollapsed(true);
    }

    const onScroll = () => {
      const y = window.scrollY;
      if (!collapsedRef.current && y > ENTER) {
        collapsedRef.current = true;
        setCollapsed(true);
      } else if (collapsedRef.current && y < EXIT) {
        collapsedRef.current = false;
        setCollapsed(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => {
    if (window.location.pathname === "/") {
      // Scroll to top naturally unwinds the morph back to wordmark
      // since the scroll listener will trip the EXIT threshold.
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Each letter is its own span so we can stagger the fade
  // right-to-left. data-l carries the letter for the transition-
  // delay rule. Order in the DOM stays left-to-right for accessibility
  // (the aria-label gives screen readers "Lembra, home").
  const letters = ["l", "e", "m", "b", "r", "a"];

  return (
    <Link
      to={logoDestination}
      onClick={handleClick}
      aria-label="Lembra, home"
      className={cn("inline-flex items-center cursor-pointer", className)}
    >
      <span
        className="lsm-wordmark"
        data-collapsed={collapsed ? "true" : "false"}
        aria-hidden="false"
      >
        <span className="lsm-letters">
          {letters.map((l) => (
            <span key={l} data-l={l}>
              {l}
            </span>
          ))}
        </span>
        <span className="lsm-dot">
          <span className="lsm-favicon" aria-hidden="true">
            l
          </span>
        </span>
      </span>
    </Link>
  );
}
